import React, { FC, useMemo } from "react";
import { Input, InputNumber, Select, Tooltip } from "antd";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { ConfigDynamicTrackerFields, ConfigStepFields } from "shared/api";
import { ConfigCardStep, ProcessStepSettingsValues } from "shared/types";
import { DynamicFieldsEnum } from "shared/constants";
import { adaptDynamicSelectValues } from "shared/adapters";
import { getSoftAndHardValues } from "shared/helpers";

interface ProcessStepSettingInputProps {
  card: ConfigCardStep;
  field: ConfigStepFields;
  fieldIndex: number;
}

const inputClassName = "w-[calc(100%+8px)] -ml-2";

const ProcessStepSettingInput: FC<ProcessStepSettingInputProps> = ({
  card,
  field,
  fieldIndex,
}) => {
  const { t } = useTranslation();
  const { getFieldProps, setFieldValue } =
    useFormikContext<ProcessStepSettingsValues>();

  const fieldName = `dynamic_process_step_fields.${card.title}.${field.name}`;
  const { value } = getFieldProps(fieldName);

  const selectOptions = useMemo(
    () => adaptDynamicSelectValues(card, fieldIndex),
    [card, fieldIndex]
  );

  const handleValueChange = (newValue: string | number | null) =>
    setFieldValue(fieldName, newValue);

  switch (field.type) {
    case DynamicFieldsEnum.TEXT:
      return (
        <Input
          size="small"
          className={inputClassName}
          value={value ?? ""}
          placeholder={t("orders.placeholder-input")!}
          onChange={(event) => handleValueChange(event.target.value)}
        />
      );
    case DynamicFieldsEnum.INTEGER:
    case DynamicFieldsEnum.FLOAT: {
      const {
        softMinimum,
        softMaximum,
        hardMinimum,
        hardMaximum,
        hideSoftHardValues,
        disableDynamicStyling,
        errorStyling,
        warningStyling,
      } = getSoftAndHardValues(
        card as unknown as ConfigDynamicTrackerFields,
        fieldIndex,
        value
      );

      return (
        <Tooltip
          title={
            hideSoftHardValues
              ? null
              : `Soft: ${softMinimum} - ${softMaximum} | Hard: ${hardMinimum} - ${hardMaximum}`
          }
        >
          <InputNumber
            size="small"
            className={classNames(inputClassName, "no-arrows", {
              "bg-red-200": !disableDynamicStyling && errorStyling,
              "bg-yellow-200": !disableDynamicStyling && warningStyling,
            })}
            value={value}
            precision={field.type === DynamicFieldsEnum.INTEGER ? 0 : undefined}
            placeholder={t("orders.placeholder-input")!}
            onChange={handleValueChange}
            onWheel={(event: React.WheelEvent<HTMLInputElement>) =>
              event.currentTarget.blur()
            }
          />
        </Tooltip>
      );
    }
    case DynamicFieldsEnum.CHOICES:
      return (
        <Select
          size="small"
          className={inputClassName}
          value={value}
          options={selectOptions}
          placeholder={t("orders.placeholder-select")}
          onChange={handleValueChange}
        />
      );
    default:
      return <span>{value ?? t("configCardColumns.noValue")}</span>;
  }
};

export { ProcessStepSettingInput };
